import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

const KEY_ENTER = 13;
const KEY_ESCAPE = 27;

/**
 * This is an 'in house' replacement component for a deprecated 3rd party
 * riek (React Inline Edit Kit) library
 */

export const InlineEdit = ({
  value,
  change,
  propName,
  className,
  classEditing,
  classInvalid,
  validate,
}) => {
  const inputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [valid, setValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [inputClassName, setInputClass] = useState();
  const [disableInput, setDisableInput] = useState(false);

  const startEditing = () => {
    setEditing(true);
  };

  const finishEditing = () => {
    const newValue = inputRef.current.value;
    if (validations(newValue) && value !== newValue) {
      setDisableInput(true);
      updateNewValue(newValue);
    } else {
      cancelEditing();
    }
  };

  const cancelEditing = () => {
    setEditing(false);
    setValid(true);
    setDisableInput(false);
  };

  const handleOnInput = (event) => {
    if (event.target.value) {
      validations(event.target.value.trim());
    }
  };

  const validations = (inValue) => {
    let inputIsValid = true;
    if (validate) {
      inputIsValid = validate(inValue);
    }
    setValid(inputIsValid);
    return inputIsValid;
  };

  const handleOnBlur = () => {
    finishEditing();
  };

  const handleKeyDown = (event) => {
    switch (event.keyCode) {
      case KEY_ENTER:
        finishEditing();
        break;
      case KEY_ESCAPE:
        cancelEditing();
        break;
      default:
        return;
    }
  };

  const handleKeyUp = () => {
    resizeInput(inputRef.current);
  };

  /**
   * This resizeInput function is implemented similarly to the original function from the riek
   * 3rd party library.
   */
  const resizeInput = (input) => {
    if (!input.startWidth) {
      input.startWidth = input.offsetWidth;
    }
    const style = input.style;
    style.width = 0; // recalculate from 0, in case characters are deleted
    let desiredWidth = input.scrollWidth;
    desiredWidth += input.offsetHeight;
    style.width = Math.max(desiredWidth, input.startWidth) + 'px';
  };

  const makeClassString = () => {
    return classNames(className, {
      [classEditing]: editing === true && classEditing,
      [classInvalid]: valid === false && classInvalid,
    });
  };

  const updateNewValue = (value) => {
    if (!validations(value)) {
      return;
    }
    setLoading(true);
    change({
      [propName]: value,
    });
  };

  useEffect(() => {
    setInputClass(makeClassString());
  }, [valid, editing]);

  useEffect(() => {
    if (loading) {
      setDisableInput(true);
    } else {
      setDisableInput(false);
    }
  }, [loading]);

  useEffect(() => {
    if (loading) {
      setLoading(false);
      setEditing(false);
      setValid(true);
    }
  }, [value]);

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  return (
    <>
      {editing !== true ? (
        <span tabIndex="0" onFocus={startEditing} onClick={startEditing}>
          {value}
        </span>
      ) : (
        <input
          defaultValue={value}
          className={inputClassName}
          onInput={handleOnInput}
          onBlur={handleOnBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          ref={inputRef}
          disabled={disableInput}
        />
      )}
    </>
  );
};
